/**
 * First-run bootstrap status and initial superadmin creation.
 *
 * @category data
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { t } from "@lingui/core/macro";

import { useAddToast } from "_/components/toasts";
import { api, ApiResponse } from "_/utils";

const SCOPE = "bootstrap";
export const KEYS = {
  status: () => [SCOPE, "status"],
};

type BootstrapStatus = {
  /** Whether the installation still needs its initial user created. */
  required: boolean;
};

type ApiErrorResponse = {
  message?: string;
};

type BootstrapArgs = {
  email: string;
  name: string;
  password: string;
  org: {
    name: string;
  };
};

type BootstrapResult = {
  user: {
    id: string;
    email: string;
    name: string;
  };
  organization: {
    id: string;
    name: string;
  };
};

/**
 * Whether the installation is fresh and still allows creating the initial
 * superadmin user.
 */
export function useBootstrapStatus() {
  async function fetchStatus(): Promise<BootstrapStatus> {
    const response = await api.get<ApiResponse<BootstrapStatus>>("/bootstrap");
    return response.data.data;
  }

  return useQuery({
    queryKey: KEYS.status(),
    queryFn: fetchStatus,
  });
}

/**
 * Create the initial organization and superadmin user on a fresh
 * installation.
 */
export function useBootstrap() {
  const queryClient = useQueryClient();
  const addToast = useAddToast();

  async function bootstrap(args: BootstrapArgs): Promise<BootstrapResult> {
    const response = await api.post<ApiResponse<BootstrapResult>>(
      "/bootstrap",
      args
    );
    return response.data.data;
  }

  return useMutation<
    BootstrapResult,
    AxiosError<ApiErrorResponse>,
    BootstrapArgs
  >({
    mutationFn: bootstrap,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KEYS.status() });
    },
    onError: (error) => {
      // Prefer the server's error message (e.g. validation failures or an
      // already-bootstrapped conflict) over the generic fallback.
      addToast({
        kind: "danger",
        title: t`common.error`,
        content:
          error.response?.data?.message ?? t`components.bootstrap.error-toast`,
      });
    },
  });
}
